import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import consultantsImage from '../assets/images/Image 4_Consultants.png';

const whatWeDo = [
  {
    icon: "🎮",
    title: "Assessment Simulators",
    text: "Deterministic simulators for Sea Wolf, Red Rock, and Casey that replicate the real assessment experience. Firm-specific and format-accurate — not generic practice.",
    link: "/seawolf"
  },
  {
    icon: "🤖",
    title: "AI Interview Coaches",
    text: "Firm-specific AI coaches for McKinsey interviewer-led and BCG candidate-led case interviews. Stage-aware, exhibit-aware, and scoring-connected.",
    link: "/mckinsey-ai-coach"
  },
  {
    icon: "📚",
    title: "Guides & Practice",
    text: "Free practice cases, consulting math guides, ecosystem solver tools, and educational resources to build skills before entering simulators.",
    link: "/redrock-guide"
  }
];

const whyCognidoor = [
  {
    icon: "🎯",
    title: "Firm-Specific, Not Generic",
    text: "Separate McKinsey and BCG tracks because the two firms test different things. Not one-size-fits-all consulting prep."
  },
  {
    icon: "📊",
    title: "Assessments + Interviews",
    text: "Most competitors cover one or the other. Cognidoor covers both digital assessments and case interview coaching in one platform."
  },
  {
    icon: "🧮",
    title: "Deterministic Simulators",
    text: "Scoring and feedback are deterministic and explainable, not LLM-hallucinated. You know exactly what you got right and why."
  },
  {
    icon: "🧠",
    title: "AI Coaches With Depth",
    text: "Not thin chatbot wrappers. Stage routing, retrieval, scoring pipelines, failure classification, and structured feedback."
  },
  {
    icon: "🔓",
    title: "Free Practice to Start",
    text: "17 free timed Red Rock practice cases with no payment required. Try before you commit."
  },
  {
    icon: "⚡",
    title: "One Account, One Platform",
    text: "Single subscription covers assessments, simulators, coaches, and progress tracking. No juggling multiple tools and accounts."
  }
];

const mckinseyTrack = [
  { name: "Sea Wolf Simulation", link: "/seawolf-practice" },
  { name: "Sea Wolf Solver", link: "/seawolf" },
  { name: "Red Rock Simulation", link: "/redrock" },
  { name: "Free Red Rock Practice", link: "/redrock-guide" },
  { name: "Ecosystem Solver", link: "/ecosystem-solver" },
  { name: "McKinsey AI Coach", link: "/mckinsey-ai-coach" }
];

const bcgTrack = [
  { name: "Casey Simulation", link: "/bcg-casey-simulation" },
  { name: "Casey Guide", link: "/bcg-casey-guide" },
  { name: "BCG AI Coach", link: "/bcg-ai-coach" }
];

/** Landing-style “Why Cognidoor?” cards: short blurbs + internal routes */
const productShowcaseCards = [
  {
    eyebrow: 'McKinsey track',
    icon: '🌊',
    accent: 'from-sky-500 via-blue-500 to-cyan-400',
    title: 'Sea Wolf simulation',
    text: 'Practice the full McKinsey-style digital assessment flow with coach feedback.',
    to: '/seawolf-practice',
  },
  {
    eyebrow: 'McKinsey track',
    icon: '🧩',
    accent: 'from-emerald-500 via-teal-500 to-cyan-400',
    title: 'Sea Wolf solver',
    text: 'Build ecosystems faster with guided solving and OCR-friendly entry.',
    to: '/seawolf',
  },
  {
    eyebrow: 'McKinsey track',
    icon: '🏔️',
    accent: 'from-indigo-500 via-sky-500 to-blue-400',
    title: 'Red Rock simulation',
    text: 'Timed study scenarios, exhibits, and scoring aligned to Red Rock-style tasks.',
    to: '/redrock',
  },
  {
    eyebrow: 'Practice',
    icon: '📘',
    accent: 'from-violet-500 via-purple-500 to-fuchsia-400',
    title: 'Free Red Rock drills',
    text: 'Seventeen free timed cases to warm up before the full simulator.',
    to: '/redrock-guide',
  },
  {
    eyebrow: 'McKinsey track',
    icon: '🌿',
    accent: 'from-green-500 via-emerald-500 to-teal-400',
    title: 'Ecosystem solver',
    text: 'Solve ecosystem-building screens with structured checks and explanations.',
    to: '/ecosystem-solver',
  },
  {
    eyebrow: 'Interview prep',
    icon: '🎙️',
    accent: 'from-sky-500 via-blue-600 to-indigo-500',
    title: 'McKinsey AI Coach',
    text: 'Interviewer-led cases, exhibits, and stage-aware coaching and scoring.',
    to: '/mckinsey-ai-coach',
  },
  {
    eyebrow: 'BCG track',
    icon: '🎯',
    accent: 'from-emerald-500 via-green-500 to-teal-400',
    title: 'BCG Casey simulation',
    text: 'Full Casey-style flow: brief, exhibits, timed questions, and feedback.',
    to: '/bcg-casey-simulation',
  },
  {
    eyebrow: 'BCG track',
    icon: '📋',
    accent: 'from-teal-500 via-cyan-500 to-sky-400',
    title: 'BCG Casey guide',
    text: 'How Casey works, what to expect, and how to structure your prep.',
    to: '/bcg-casey-guide',
  },
  {
    eyebrow: 'Interview prep',
    icon: '💬',
    accent: 'from-lime-600 via-emerald-600 to-green-500',
    title: 'BCG AI Coach',
    text: 'Candidate-led practice with structured feedback between stages.',
    to: '/bcg-ai-coach',
  },
  {
    eyebrow: 'Skills',
    icon: '📐',
    accent: 'from-amber-500 via-orange-500 to-rose-400',
    title: 'Math & chart guides',
    text: 'Consulting math and chart reading to support every product above.',
    to: '/consulting-math-guides',
  },
];

const heroQuickLinkClass =
  'inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold border-2 border-white/40 text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="bg-white text-gray-900 font-sans">
      <SEO
        title="About Cognidoor – McKinsey & BCG Prep Platform"
        description="Practice McKinsey Solve and BCG Casey, and prepare for case interviews on one platform. Get real-time AI coaching for case interviews, plus guided simulations for Solve and Casey that build the thinking, structure, and confidence you'll need on the real test."
        url="/about"
      />

      {/* Hero Section */}
      <section className="relative z-30 bg-gradient-to-br from-blue-100 via-blue-50 to-white pb-6 sm:pb-12 min-h-[320px] sm:min-h-[500px] flex items-center overflow-visible">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-700/80">
          <div className="absolute inset-0" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'overlay'
          }} />
        </div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="absolute z-10 hidden md:block hero-image w-[200px] md:w-[300px] lg:w-[400px] aspect-[2/3]"
          style={{ right: '2vw', top: '24px' }}
        >
          <div className="h-full rounded-xl overflow-hidden shadow-2xl transform hover:scale-[1.02] transition-transform duration-500">
            <img
              src={consultantsImage}
              alt="Consultants preparing for interviews"
              className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
            />
          </div>
        </motion.div>
        <div className="container pl-3 sm:pl-[36px] md:pl-[72px] grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 py-10 sm:py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl hero-content"
          >
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 leading-tight text-white">
              About Cognidoor
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-white/90 mb-6 sm:mb-10 leading-relaxed">
              Firm-specific consulting interview and assessment prep for McKinsey and BCG candidates. One platform for assessments, simulators, AI coaches, and progress tracking.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  const el = document.getElementById('cognidoor-products');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                className="group relative px-5 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
              >
                Explore products
                <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              </motion.button>
              <Link to="/mckinsey-ai-coach" className={heroQuickLinkClass}>
                McKinsey AI Coach
              </Link>
              <Link to="/bcg-ai-coach" className={heroQuickLinkClass}>
                BCG AI Coach
              </Link>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
              <Link to="/about-solve" className="text-sm text-white/90 underline-offset-4 hover:text-white hover:underline">
                About McKinsey Solve
              </Link>
              <span className="text-white/40 hidden sm:inline" aria-hidden>
                ·
              </span>
              <Link to="/about-bcg-casey" className="text-sm text-white/90 underline-offset-4 hover:text-white hover:underline">
                About BCG Casey
              </Link>
              <span className="text-white/40 hidden sm:inline" aria-hidden>
                ·
              </span>
              <Link to="/#pricing" className="text-sm text-white/90 underline-offset-4 hover:text-white hover:underline">
                Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product showcase — same card language as landing “Why Cognidoor?” */}
      <section
        id="cognidoor-products"
        className="py-12 sm:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-4 sm:px-6 md:px-10 xl:px-20 relative"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.1),transparent_50%)]" />
        </div>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 tracking-tight relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900">
          Cognidoor products
        </h2>
        <div className="mx-auto mb-6 h-0.5 w-24 rounded-full bg-gradient-to-r from-transparent via-sky-400/70 to-transparent relative z-10" />
        <p className="max-w-3xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed mb-10 sm:mb-12 relative z-10">
          One platform for McKinsey Solve, BCG Casey, and firm-specific case interview coaching. Tap a card to open the product.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto items-stretch relative z-10 text-left">
          {productShowcaseCards.map((card, index) => (
            <Link
              key={card.to}
              to={card.to}
              className="group rounded-[28px] shadow-md p-5 sm:p-8 md:p-9 backdrop-blur-sm min-h-[220px] sm:min-h-[260px] flex flex-col relative overflow-hidden border border-slate-200/60 bg-white/90 hover:shadow-lg hover:border-sky-200/50 transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              style={{ animationDelay: `${index * 0.06}s` }}
            >
              <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                <div
                  className={`absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gradient-to-br ${card.accent} blur-3xl opacity-20`}
                />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-300/80 to-transparent" />
              </div>
              <div
                className={`h-1.5 w-24 rounded-full bg-gradient-to-r ${card.accent} transition-all duration-500 group-hover:w-32 group-hover:translate-x-1`}
              />
              <div className="mt-6 flex items-center gap-4">
                <div className="feature-icon w-14 h-14 text-2xl mb-0 transition-all duration-500 group-hover:scale-110 group-hover:-rotate-6 group-hover:shadow-[0_14px_30px_-14px_rgba(59,130,246,0.45)] flex items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-blue-100 border border-sky-100/80">
                  {card.icon}
                </div>
                <div className="text-xs sm:text-sm font-semibold uppercase tracking-[0.18em] text-sky-700 transition-transform duration-500 group-hover:translate-x-1">
                  {card.eyebrow}
                </div>
              </div>
              <h3 className="mt-6 text-xl sm:text-2xl font-semibold leading-tight text-gray-800 transition-colors duration-300 group-hover:text-slate-900">
                {card.title}
              </h3>
              <p className="mt-4 text-[15px] sm:text-base text-gray-600 leading-relaxed transition-colors duration-300 group-hover:text-slate-700 flex-1">
                {card.text}
              </p>
              <span className="mt-4 text-sm font-semibold text-sky-600 group-hover:text-sky-700 inline-flex items-center gap-1">
                Open
                <span aria-hidden>→</span>
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* What Cognidoor Does */}
      <section id="what-we-do" className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white text-center px-3 sm:px-6 md:px-10 relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-12 section-title tracking-tight">What Cognidoor Does</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
          {whatWeDo.map((item, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(item.link)}
              className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm cursor-pointer"
            >
              <div className="feature-icon">{item.icon}</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{item.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              <div className="learn-more-text">Learn More</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why Cognidoor */}
      <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white px-3 sm:px-6 md:px-10 text-center relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-12 section-title tracking-tight">Why Cognidoor</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-7xl mx-auto">
          {whyCognidoor.map((item, idx) => (
            <div key={idx} className="feature-card rounded-2xl shadow-md p-4 sm:p-6 md:p-8 backdrop-blur-sm" style={{ animationDelay: `${idx * 0.1}s` }}>
              <div className="feature-icon">{item.icon}</div>
              <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{item.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* McKinsey & BCG Tracks */}
      <section id="tracks" className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/30 to-white px-3 sm:px-6 md:px-10 relative text-center">
        <div className="w-full max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 section-title tracking-tight">McKinsey & BCG Tracks</h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto mb-8 sm:mb-12">
            Cognidoor separates prep by firm because McKinsey and BCG use different assessment formats and interview styles. Each track is built around the specific tools and skills that firm requires.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-24px" }}
            transition={{ duration: 0.4 }}
            className="feature-card rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 p-6 sm:p-8 backdrop-blur-sm bg-white/95 hover:shadow-xl hover:border-sky-100 transition-all duration-300"
          >
            <h3 className="text-lg sm:text-xl font-bold mb-5 text-gray-800 flex items-center gap-2">
              <span className="text-sky-500">McKinsey</span> Track
            </h3>
            <div className="flex flex-wrap gap-3">
              {mckinseyTrack.map((item, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(item.link)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-sky-50 text-sky-700 border border-sky-200/80 hover:bg-sky-100 hover:border-sky-300 hover:text-sky-800 transition-all duration-200 shadow-sm"
                >
                  {item.name}
                  <span className="text-sky-400" aria-hidden>→</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-24px" }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="feature-card rounded-2xl shadow-lg shadow-slate-200/60 border border-slate-100 p-6 sm:p-8 backdrop-blur-sm bg-white/95 hover:shadow-xl hover:border-emerald-100 transition-all duration-300"
          >
            <h3 className="text-lg sm:text-xl font-bold mb-5 text-gray-800 flex items-center gap-2">
              <span className="text-emerald-600">BCG</span> Track
            </h3>
            <div className="flex flex-wrap gap-3">
              {bcgTrack.map((item, idx) => (
                <motion.button
                  key={idx}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(item.link)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200/80 hover:bg-emerald-100 hover:border-emerald-300 hover:text-emerald-800 transition-all duration-200 shadow-sm"
                >
                  {item.name}
                  <span className="text-emerald-500" aria-hidden>→</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 sm:py-20 bg-gradient-to-b from-white via-blue-50/20 to-white text-center px-3 sm:px-6 md:px-10 relative">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 section-title tracking-tight">Ready to Start?</h2>
        <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto mb-6 sm:mb-10">
          Try free Red Rock practice cases with no account required, or explore our full platform with AI coaches, simulators, and progress tracking.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/auth')}
            className="group relative px-6 py-3 sm:px-8 sm:py-4 bg-gradient-to-r from-blue-600 to-blue-400 text-white text-base sm:text-lg rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
          >
            Start Preparing
            <div className="absolute inset-0 bg-white rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/#pricing')}
            className="px-6 py-3 sm:px-8 sm:py-4 border-2 border-blue-500 text-blue-600 text-base sm:text-lg rounded-xl hover:bg-blue-50 transition-all duration-300"
          >
            See Pricing
          </motion.button>
        </div>
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-2xl mx-auto">
          <Link
            to="/mckinsey-ai-coach"
            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-sky-50 text-sky-800 border border-sky-200 hover:bg-sky-100 transition-colors"
          >
            McKinsey AI Coach
          </Link>
          <Link
            to="/bcg-ai-coach"
            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 transition-colors"
          >
            BCG AI Coach
          </Link>
          <Link
            to="/about-solve"
            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-white text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors"
          >
            About Solve
          </Link>
          <Link
            to="/about-bcg-casey"
            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-white text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors"
          >
            About Casey
          </Link>
          <Link
            to="/redrock-guide"
            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium bg-white text-slate-700 border border-slate-200 hover:border-slate-300 transition-colors"
          >
            Free Red Rock practice
          </Link>
        </div>
      </section>
    </div>
  );
}
